import { GraduationCap } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';

import { SchoolTournament } from './SchoolTournament';

import TDEP from '@/assets/TDEP.jpg';
import TDES from '@/assets/TDES.jpg';
import { Button } from '@/components';
import { Layout } from '@/components/GenericsTournaments';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { useMetaData } from '@/hooks/useMetaData';
import useToast from '@/hooks/useToast';
import { tdeInit, tdeSchoolData } from '@/services/tde';
import { ResponseTdeInit, ResponseTdeRankingData } from '@/types/tde';
import { formatearNumero } from '@/utils';
import { SECTION_NAME_FOR_ADS } from '@/utils/ads';

const tournamentsData = [
  {
    name: 'Torneo de ',
    namePart2: 'Escuelas Primarias',
    title: 'Escuelas Primarias',
    schools: 19789,
    students: 19789,
    image: TDEP,
    color: 'success',
    key: 'primarias',
  },
  {
    name: 'Torneo de ',
    namePart2: 'Escuelas Secundarias',
    title: 'Escuelas Secundarias',
    schools: 19789,
    students: 19789,
    image: TDES,
    color: 'primary',
    key: 'secundarias',
  },
];

interface TournamentPageProps {
  setPageSelected: (page: number) => void;
  dataInit?: ResponseTdeInit;
  schooldata?: ResponseTdeRankingData;
  isLoading: boolean;
  schoolIsLoading: boolean;
}

// Componente para la Landing Page (pageSelected === 0)
const LandingSchoolTournament = ({
  setPageSelected,
  dataInit,
  isDesktop,
}: {
  setPageSelected: (page: number) => void;
  dataInit?: ResponseTdeInit;
  isDesktop: boolean;
}) => {
  return (
    <Layout
      adsSections={SECTION_NAME_FOR_ADS.torneodeescuelas}
      className="px-5 pt-2"
      main={() => (
        <div className="container max-w-5xl mx-auto lg:px-5">
          <div className="flex mt-10 gap-2 items-center max-md:hidden">
            <div className="w-8 h-8 bg-gdt-blue-light2 flex justify-center items-center rounded-full">
              <GraduationCap className="text-gdt-blue" size={24} />
            </div>
            <span className="text-2xl font-bold text-black">
              Torneos de Escuelas
            </span>
          </div>
          <p className="font-semibold text-gdt-gray-2 max-w-3xl ml-2  md:ml-11 mt-3 md:mt-0">
            Los Torneos de Escuelas son torneos en los cuales pueden competir
            todos los participantes que estén cursando la escuela primaria o
            secundaria.
          </p>

          <div className="md:max-w-3xl md:ml-11 flex flex-wrap justify-between mt-6">
            {tournamentsData.map((tournament, index) =>
              isDesktop ? (
                <div
                  key={tournament.title}
                  className="rounded-lg bg-white max-md:w-full mb-6"
                >
                  <div className="relative">
                    <img
                      alt="torneo escuelas primarias"
                      className="max-md:hidden"
                      src={tournament.image}
                    />
                    <p className="absolute bottom-0 left-5 text-white py-2 font-bebas font-light text-4xl leading-10 max-md:hidden">
                      {tournament.name}
                      <br />
                      {tournament.namePart2}
                    </p>
                  </div>
                  <div className="flex justify-start items-start  gap-3 ml-14">
                    <div className="mt-7">
                      <div
                        className={`w-8 h-8 flex md:hidden justify-center items-center rounded-full ${
                          tournament.color === 'success'
                            ? 'bg-gdt-status-green-light'
                            : 'bg-gdt-blue-light2'
                        }`}
                      >
                        <GraduationCap
                          className={
                            tournament.color === 'success'
                              ? 'text-gdt-status-green'
                              : 'text-gdt-blue'
                          }
                          size={24}
                        />
                      </div>
                    </div>
                    <div className="flex flex-col pt-6 gap-1">
                      <span className="font-bold text-black pb-1">
                        <span className="md:hidden">{tournament.name}</span>
                        {tournament.title}
                      </span>
                      <span className="text-gdt-gray-3 text-sm">{`Escuelas participando: ${formatearNumero(
                        dataInit?.cantParticipantes[
                          tournament.key as 'primarias' | 'secundarias'
                        ]?.escuelas ?? 0
                      )}`}</span>
                      <span className="text-gdt-gray-3 text-sm">{`Chicos participando: ${formatearNumero(
                        dataInit?.cantParticipantes[
                          tournament.key as 'primarias' | 'secundarias'
                        ]?.alumnos ?? 0
                      )}`}</span>
                    </div>
                  </div>
                  <div className="flex mt-5 mb-6">
                    <Button
                      className={`mx-auto ${
                        tournament.color === 'success' &&
                        'max-md:bg-gdt-status-green max-md:border-gdt-status-green'
                      }`}
                      type="button"
                      variant="primary"
                      onClick={() => setPageSelected(index + 1)}
                    >
                      Ver Torneo
                    </Button>
                  </div>
                </div>
              ) : (
                <div
                  key={tournament.title}
                  className="bg-white rounded-lg mb-6 p-6 max-md:w-full"
                >
                  <div className="flex">
                    <div
                      className={`w-8 h-8 mr-4 flex justify-center items-center rounded-full ${
                        tournament.color === 'success'
                          ? 'bg-gdt-status-green-light'
                          : 'bg-gdt-blue-light2'
                      }`}
                    >
                      <GraduationCap
                        className={
                          tournament.color === 'success'
                            ? 'text-gdt-status-green'
                            : 'text-gdt-blue'
                        }
                        size={24}
                      />
                    </div>
                    <div>
                      <p className="font-bold text-black pb-1 text-[14px] whitespace-nowrap">
                        <span className="md:hidden">{tournament.name}</span>
                        {tournament.title}
                      </p>
                      <p className="font-medium text-[14px] text-[#4F4F4F]">{`Escuelas participando: ${formatearNumero(
                        dataInit?.cantParticipantes[
                          tournament.key as 'primarias' | 'secundarias'
                        ]?.escuelas ?? 0
                      )}`}</p>
                      <p className="font-semibold text-[12px] text-[#A09EB3]">{`Chicos participando: ${formatearNumero(
                        dataInit?.cantParticipantes[
                          tournament.key as 'primarias' | 'secundarias'
                        ]?.alumnos ?? 0
                      )}`}</p>
                    </div>
                  </div>
                  <div className="flex justify-center mt-4">
                    <Button
                      className={`w-[115px] h-[36px] px-0 text-xs border-none ${
                        tournament.color === 'success'
                          ? 'bg-gdt-status-green border-gdt-status-green'
                          : 'bg-gdt-blue border-gdt-blue'
                      }`}
                      type="button"
                      variant="primary"
                      onClick={() => setPageSelected(index + 1)}
                    >
                      Ver Torneo
                    </Button>
                  </div>
                </div>
              )
            )}
          </div>
        </div>
      )}
      mobile={{
        leftContent: {
          showMenuAction: true,
          items: true,
        },
        centerContent: {
          title: 'Torneo de Escuelas',
        },
      }}
      trackProps={{
        escuela: dataInit?.nombreEscuela || '',
        primaria: dataInit?.participa === 3,
        secundario: dataInit?.participa === 2,
      }}
    />
  );
};

// Componente para torneos de Escuelas Primarias (pageSelected === 1)
const PrimarySchoolTournament = ({
  setPageSelected,
  dataInit,
  schooldata,
  isLoading,
  schoolIsLoading,
}: TournamentPageProps) => {
  return (
    <SchoolTournament
      key="primary-school-tournament"
      backButtonAction={() => setPageSelected(0)}
      changeSchoolType={(num) => setPageSelected(num)}
      dataInit={dataInit}
      isLoading={isLoading}
      pageSelected={1}
      schoolIsLoading={schoolIsLoading}
      schooldata={schooldata}
    />
  );
};

// Componente para torneos de Escuelas Secundarias (pageSelected === 2)
const SecondarySchoolTournament = ({
  setPageSelected,
  dataInit,
  schooldata,
  isLoading,
  schoolIsLoading,
}: TournamentPageProps) => {
  return (
    <SchoolTournament
      key="secondary-school-tournament"
      backButtonAction={() => setPageSelected(0)}
      changeSchoolType={(num) => setPageSelected(num)}
      dataInit={dataInit}
      isLoading={isLoading}
      pageSelected={2}
      schoolIsLoading={schoolIsLoading}
      schooldata={schooldata}
    />
  );
};

export const TDEpage = () => {
  const [pageSelected, setPageSelected] = useState(0);
  const { isDesktop } = useIsDesktop();
  const toast = useToast();
  useMetaData({
    title: 'Torneo de Escuelas',
  });

  const { data: dataInit, isLoading } = useQuery(
    'initDataTde',
    async () => await tdeInit(),
    {
      refetchOnWindowFocus: false,
      retry: false,
      onSuccess: (data) => {
        if ('codError' in data && 'mensaje' in data) {
          toast.show(data.mensaje as string);
        }
      },
      onError: (error) => {
        const err = error as Error;
        toast.show(err.message);
      },
    }
  );

  useEffect(() => {
    if (dataInit?.participa === 1) {
      setPageSelected(1);
    } else if (dataInit?.participa === 2) {
      setPageSelected(2);
    } else if (dataInit?.participa === 3) {
      setPageSelected(0);
    }
  }, [dataInit]);

  const { data: schooldata, isLoading: schoolIsLoading } = useQuery(
    ['schoolDataTde', pageSelected],
    async () => await tdeSchoolData(pageSelected === 1),
    {
      refetchOnWindowFocus: false,
      retry: false,
      enabled: !!dataInit, // Solo se ejecuta cuando dataInit está disponible
      onSuccess: (data) => {
        if ('codError' in data && 'mensaje' in data) {
          toast.show(data.mensaje as string);
        }
      },
      onError: (error) => {
        const err = error as Error;
        toast.show(err.message);
      },
    }
  );

  switch (pageSelected) {
    case 1:
      return (
        <PrimarySchoolTournament
          dataInit={dataInit}
          isLoading={isLoading}
          schoolIsLoading={schoolIsLoading}
          schooldata={schooldata}
          setPageSelected={setPageSelected}
        />
      );
    case 2:
      return (
        <SecondarySchoolTournament
          dataInit={dataInit}
          isLoading={isLoading}
          schoolIsLoading={schoolIsLoading}
          schooldata={schooldata}
          setPageSelected={setPageSelected}
        />
      );
    default:
      return (
        <LandingSchoolTournament
          dataInit={dataInit}
          isDesktop={isDesktop}
          setPageSelected={setPageSelected}
        />
      );
  }
};
